import { useState, useEffect } from "react";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { Label } from "../../components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import { getAuditLog } from "../../lib/api";

const getActionIcon = (action) => {
  switch(action) {
    case "login_success": return <span className="text-green-500">&#10003;</span>;
    case "login_failed": return <span className="text-red-500">&#10007;</span>;
    case "student_create": return <span className="text-blue-500">+</span>;
    case "student_update": return <span className="text-orange-500">&#9998;</span>;
    case "student_delete": return <span className="text-red-500">&minus;</span>;
    case "student_reenroll": return <span className="text-purple-500">&#8635;</span>;
    default: return <span className="text-gray-500">&bull;</span>;
  }
};

const getActionLabel = (action) => {
  const labels = {
    login_success: "Login", login_failed: "Failed Login", student_create: "Student Created",
    student_update: "Student Updated", student_delete: "Student Deleted",
    student_reenroll: "Student Re-enrolled", data_export: "Data Export",
    archive_students: "Students Archived", start_new_year: "New Year Started",
  };
  return labels[action] || action;
};

export function AuditLogTab() {
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all");

  const fetchLogs = async () => {
    setLoading(true);
    try { const res = await getAuditLog(100, filter === "all" ? null : filter); setLogs(res.data); } catch { /* silent */ } finally { setLoading(false); }
  };

  useEffect(() => { fetchLogs(); }, [filter]);

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle>Audit Log</CardTitle>
          <CardDescription>Track staff logins and student record changes</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex items-center gap-4">
            <div className="space-y-2">
              <Label>Filter by Action</Label>
              <Select value={filter} onValueChange={setFilter}>
                <SelectTrigger className="w-48" data-testid="audit-filter"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Actions</SelectItem>
                  <SelectItem value="login_success">Successful Logins</SelectItem>
                  <SelectItem value="login_failed">Failed Logins</SelectItem>
                  <SelectItem value="student_create">Student Created</SelectItem>
                  <SelectItem value="student_update">Student Updated</SelectItem>
                  <SelectItem value="student_delete">Student Deleted</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <Button variant="outline" onClick={fetchLogs} className="mt-6">Refresh</Button>
          </div>
          {loading ? (
            <div className="text-center py-8 text-muted-foreground">Loading audit logs...</div>
          ) : logs.length === 0 ? (
            <div className="text-center py-8 text-muted-foreground border rounded-lg">No audit log entries found</div>
          ) : (
            <div className="border rounded-lg max-h-[500px] overflow-y-auto">
              <table className="w-full text-sm">
                <thead className="bg-muted sticky top-0">
                  <tr><th className="p-3 text-left">Time</th><th className="p-3 text-left">Action</th><th className="p-3 text-left">User</th><th className="p-3 text-left">Details</th></tr>
                </thead>
                <tbody>
                  {logs.map((log) => {
                    const action = typeof log.action === 'string' ? log.action : '';
                    const actionColor = action.includes('failed') ? 'bg-red-100 text-red-700' :
                      action.includes('login') ? 'bg-green-100 text-green-700' :
                      action.includes('create') ? 'bg-blue-100 text-blue-700' :
                      action.includes('update') ? 'bg-orange-100 text-orange-700' :
                      action.includes('delete') ? 'bg-red-100 text-red-700' : 'bg-gray-100 text-gray-700';
                    return (
                      <tr key={log.id} className="border-t hover:bg-muted/50">
                        <td className="p-3 text-muted-foreground whitespace-nowrap">{log.timestamp ? new Date(log.timestamp).toLocaleString() : '-'}</td>
                        <td className="p-3">
                          <span className="flex items-center gap-2">
                            {getActionIcon(action)}
                            <span className={`text-xs px-2 py-0.5 rounded ${actionColor}`}>{getActionLabel(action)}</span>
                          </span>
                        </td>
                        <td className="p-3 font-medium">{typeof log.user_name === 'string' ? log.user_name : '-'}</td>
                        <td className="p-3 text-muted-foreground">{typeof log.details === 'string' ? log.details : '-'}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
